import { useState } from "react";
import { Pencil, Save, X } from "lucide-react";
import { useAuth } from "../auth/useAuth";
import { getDocFields, putOverride } from "../lib/overlay";

const FIELDS: { key: string; label: string; rows: number }[] = [
  { key: "lead", label: "Intro", rows: 4 },
  { key: "build", label: "The build, end to end", rows: 6 },
  { key: "tools", label: "What changed how I work", rows: 10 },
  { key: "howfar", label: "How far it's come", rows: 5 },
  { key: "oneshot", label: "One-shot data modeling", rows: 4 },
  { key: "systems", label: "Systems thinking vs defect fixing", rows: 5 },
  { key: "impacts", label: "Impacts", rows: 6 },
  { key: "conclusions", label: "Conclusions", rows: 8 },
];

/** Admin-only affordance on the About page: edit the first-person prose, persisted as a published doc. */
export function AdminEditAbout({ id, defaults }: { id: string; defaults: Record<string, string> }) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  if (!isAdmin) return null;
  return editing ? (
    <AboutEditor id={id} defaults={defaults} onClose={() => setEditing(false)} />
  ) : (
    <div className="eng-edit-bar">
      <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
        <Pencil size={14} /> Edit this page
      </button>
    </div>
  );
}

function AboutEditor({
  id,
  defaults,
  onClose,
}: {
  id: string;
  defaults: Record<string, string>;
  onClose: () => void;
}) {
  const { getApiToken } = useAuth();
  const current = { ...defaults, ...getDocFields(id) };
  const [vals, setVals] = useState<Record<string, string>>(() => ({ ...current }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    const token = await getApiToken();
    if (!token) {
      setErr("Couldn't get an access token — try signing in again.");
      setSaving(false);
      return;
    }
    const fields: Record<string, string> = {};
    for (const { key } of FIELDS) if (vals[key] !== current[key]) fields[key] = vals[key] ?? "";
    // The About page is always public, so persist it as published.
    const ok = await putOverride(id, { fields, stage: "published" }, token);
    setSaving(false);
    if (ok) onClose();
    else setErr("Save failed — please retry.");
  };

  return (
    <div className="eng-editor card card-pad">
      <div className="eng-editor-head">
        <strong>Edit this page</strong>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          aria-label="Close"
          style={{ marginLeft: "auto" }}
        >
          <X size={16} />
        </button>
      </div>
      {FIELDS.map(({ key, label, rows }) => (
        <label className="eng-field" key={key}>
          <span>{label}</span>
          <textarea
            rows={rows}
            value={vals[key] ?? ""}
            onChange={(e) => setVals((s) => ({ ...s, [key]: e.target.value }))}
          />
        </label>
      ))}
      {err && <p className="eng-err">{err}</p>}
      <div className="eng-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
